import type { Exercise } from '../exercise-types';

/** Quads, hamstrings, glutes, calves and adductors. */
export const LEG_EXERCISES: Exercise[] = [
  // -------------------------------------------------------------- quads -----
  {
    id: 'back_squat',
    name: 'Barbell back squat',
    slots: ['quad_primary'],
    pattern: 'squat',
    primary: ['quads', 'glutes'],
    secondary: ['hamstrings', 'lower_back', 'abs', 'adductors'],
    equipment: ['barbell', 'power_rack'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 3,
    quality: 10,
    defaultSets: 4,
    repRange: [5, 8],
    restSeconds: 210,
    incrementKg: 2.5,
    setup: [
      'Bar on your upper traps (high bar) or across the rear delts (low bar). Set the safeties.',
      'Unrack, walk back two steps, feet shoulder width with toes slightly out.',
      'Big breath into the belly, brace like you are about to be punched.',
    ],
    cues: [
      'Sit down and slightly back, knees tracking over the toes, chest proud.',
      'Go to at least parallel — hip crease level with the top of the knee.',
      'Drive the whole foot through the floor and keep the bar over your midfoot the whole way.',
    ],
    mistakes: ['Knees caving in', 'Hips shooting up first out of the hole'],
    variations: ['leg_press', 'hack_squat', 'front_squat', 'smith_squat'],
    imagery: {
      start: 'standing upright with a barbell across the upper back, feet shoulder width, braced, side view',
      end: 'squatted down to below parallel with a barbell on the back, hips below knee level, chest up',
    },
  },
  {
    id: 'front_squat',
    name: 'Front squat',
    slots: ['quad_primary'],
    pattern: 'squat',
    primary: ['quads'],
    secondary: ['glutes', 'abs', 'upper_back'],
    equipment: ['barbell', 'power_rack'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 3,
    quality: 9,
    defaultSets: 4,
    repRange: [6, 10],
    restSeconds: 180,
    incrementKg: 2.5,
    setup: [
      'Bar racked across the front delts, elbows driven high, fingers under the bar.',
      'Feet shoulder width, upright torso.',
    ],
    cues: [
      'Elbows up is the whole lift — the moment they drop, the bar rolls off.',
      'More quad, less lower back than a back squat. Expect ~70% of your back squat weight.',
    ],
    variations: ['back_squat', 'hack_squat', 'goblet_squat'],
    imagery: {
      start: 'standing with a barbell racked across the front shoulders, elbows high and pointing forward',
      end: 'front squat bottom position, hips below parallel, torso upright, elbows still high',
    },
  },
  {
    id: 'leg_press',
    name: 'Leg press',
    slots: ['quad_primary'],
    pattern: 'squat',
    primary: ['quads', 'glutes'],
    secondary: ['hamstrings', 'adductors'],
    equipment: ['leg_press'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 4,
    repRange: [8, 15],
    restSeconds: 150,
    incrementKg: 10,
    setup: [
      'Feet shoulder width, mid-platform. Lower and narrower biases quads, higher and wider biases glutes.',
      'Back and hips flat against the pad.',
    ],
    cues: [
      'Lower until your knees reach about 90° or a touch deeper — stop before your lower back rounds off the pad.',
      'Push through the whole foot, never lock the knees out hard at the top.',
    ],
    mistakes: ['Letting the lower back curl under at the bottom', 'Slamming into lockout'],
    variations: ['hack_squat', 'back_squat', 'smith_squat'],
    imagery: {
      start: 'seated in a leg press machine with legs extended pushing the platform away, knees soft',
      end: 'leg press platform lowered with knees bent to 90 degrees towards the chest, back flat on the pad',
    },
  },
  {
    id: 'hack_squat',
    name: 'Hack squat',
    slots: ['quad_primary'],
    pattern: 'squat',
    primary: ['quads'],
    secondary: ['glutes'],
    equipment: ['hack_squat'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 150,
    incrementKg: 10,
    setup: ['Shoulders under the pads, back flat on the backrest, feet low and shoulder width for quads.'],
    cues: [
      'Sit down deep, knees travelling forward over the toes — that is safe here and it is what hits the quads.',
      'The fixed path means you can go closer to failure than on a free squat.',
    ],
    variations: ['back_squat', 'leg_press', 'front_squat'],
    imagery: {
      start: 'standing in a hack squat machine with shoulders under the pads and legs extended',
      end: 'hack squat bottom position, knees deeply bent and travelling forward, back flat on the pad',
    },
  },
  {
    id: 'smith_squat',
    name: 'Smith machine squat',
    slots: ['quad_primary', 'quad_secondary'],
    pattern: 'squat',
    primary: ['quads', 'glutes'],
    secondary: ['hamstrings'],
    equipment: ['smith_machine'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 120,
    incrementKg: 2.5,
    setup: ['Bar on the traps, feet set slightly forward of the bar so you can sit straight down.'],
    cues: ['The forward foot position is what makes this work — it lets you stay upright and load the quads.'],
    variations: ['back_squat', 'hack_squat'],
    imagery: {
      start: 'standing under a smith machine bar with feet placed slightly forward of the body, bar on the traps',
      end: 'squatted to below parallel under a smith machine bar, torso upright, feet forward',
    },
  },
  {
    id: 'bulgarian_split_squat',
    name: 'Bulgarian split squat',
    slots: ['quad_secondary'],
    pattern: 'lunge',
    primary: ['quads', 'glutes'],
    secondary: ['hamstrings', 'adductors'],
    equipment: ['dumbbells', 'flat_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 9,
    unilateral: true,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 2,
    setup: [
      'Rear foot laces-down on the bench, front foot about two feet forward.',
      'Dumbbells at your sides.',
    ],
    cues: [
      'Drop straight down until the back knee nearly touches the floor.',
      'Upright torso for quads, slight forward lean for glutes.',
      'Easily the most honest leg exercise there is. It will humble you.',
    ],
    mistakes: ['Front foot too close, which jams the knee'],
    variations: ['walking_lunge', 'step_up', 'leg_press'],
    imagery: {
      start: 'standing in a split stance with the rear foot elevated on a bench, dumbbells at the sides, leg straight',
      end: 'bottom of a bulgarian split squat, rear knee close to the floor, front thigh parallel, dumbbells at sides',
    },
  },
  {
    id: 'walking_lunge',
    name: 'Walking lunge',
    slots: ['quad_secondary', 'finisher'],
    pattern: 'lunge',
    primary: ['quads', 'glutes'],
    secondary: ['hamstrings', 'adductors'],
    equipment: ['dumbbells'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 8,
    unilateral: true,
    defaultSets: 3,
    repRange: [10, 16],
    restSeconds: 120,
    incrementKg: 2,
    setup: ['Dumbbells at your sides, stand tall with room to walk.'],
    cues: [
      'Long steps hit glutes and hamstrings, short steps hit quads.',
      'Back knee lightly touches, torso stays upright. Count reps per leg.',
    ],
    variations: ['bulgarian_split_squat', 'step_up'],
    imagery: {
      start: 'standing upright holding dumbbells at the sides about to step forward into a lunge',
      end: 'deep forward lunge position with the back knee near the floor and front thigh parallel, dumbbells at sides',
    },
  },
  {
    id: 'step_up',
    name: 'Dumbbell step-up',
    slots: ['quad_secondary'],
    pattern: 'lunge',
    primary: ['quads', 'glutes'],
    secondary: ['hamstrings'],
    equipment: ['dumbbells', 'plyo_box'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 8,
    unilateral: true,
    defaultSets: 3,
    repRange: [10, 12],
    restSeconds: 90,
    incrementKg: 2,
    setup: ['Box at roughly knee height, dumbbells at your sides.'],
    cues: [
      'Drive through the heel of the top foot; do not push off the bottom foot.',
      'Lower back down slowly rather than dropping.',
    ],
    variations: ['bulgarian_split_squat', 'walking_lunge'],
    imagery: {
      start: 'standing in front of a plyo box with one foot planted on top of it, dumbbells held at the sides',
      end: 'standing tall on top of a plyo box on one leg, other leg lifted, dumbbells at the sides',
    },
  },
  {
    id: 'goblet_squat',
    name: 'Goblet squat',
    slots: ['quad_secondary'],
    pattern: 'squat',
    primary: ['quads', 'glutes'],
    secondary: ['abs', 'adductors'],
    equipment: ['kettlebells'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 90,
    incrementKg: 4,
    setup: ['Hold a kettlebell by the horns against your chest, feet shoulder width.'],
    cues: ['The front load keeps you upright automatically. Squat deep, elbows inside the knees.'],
    variations: ['front_squat', 'smith_squat'],
    imagery: {
      start: 'standing holding a kettlebell against the chest with both hands, feet shoulder width',
      end: 'deep squat holding a kettlebell at the chest, elbows inside the knees, torso upright',
    },
  },
  {
    id: 'leg_extension',
    name: 'Leg extension',
    slots: ['quad_isolation'],
    pattern: 'isolation',
    primary: ['quads'],
    secondary: [],
    equipment: ['leg_extension'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 75,
    incrementKg: 5,
    setup: ['Knee pivot lined up with the machine axis, pad on the lower shin above the ankle.'],
    cues: [
      'Extend to straight and hold the squeeze for a full second.',
      'Lower over three seconds. The only exercise that isolates the rectus femoris properly.',
    ],
    mistakes: ['Slamming into lockout with too much weight'],
    variations: ['sissy_squat', 'reverse_nordic', 'hack_squat'],
    imagery: {
      start: 'seated in a leg extension machine with knees bent at 90 degrees and pads against the lower shins',
      end: 'seated with legs fully extended straight out in a leg extension machine, quads contracted',
    },
  },
  {
    id: 'sissy_squat',
    name: 'Sissy squat',
    slots: ['quad_isolation'],
    pattern: 'isolation',
    primary: ['quads'],
    secondary: ['abs'],
    equipment: ['power_rack', 'bodyweight'],
    kind: 'isolation',
    loadType: 'bodyweight',
    difficulty: 3,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 75,
    incrementKg: 0,
    setup: [
      'Hold a rack upright with one hand, feet hip width, heels raised slightly if you can.',
      'Stand tall with the hips pushed forward.',
    ],
    cues: [
      'Lean back and let the knees travel forward while keeping hips, torso and head in one straight line.',
      'The hips never bend — that is what makes it isolate the quads instead of turning into a squat.',
      'Go only as deep as you can control. The stretch on the rectus femoris here is enormous.',
    ],
    mistakes: ['Letting the hips fold, which turns it into a normal squat'],
    variations: ['leg_extension', 'reverse_nordic'],
    imagery: {
      start: 'standing tall beside a rack upright holding it with one hand, hips pushed forward, legs straight',
      end: 'leaning back with knees bent and travelling far forward, body in one straight line from knee to head',
    },
  },
  {
    id: 'reverse_nordic',
    name: 'Reverse Nordic curl',
    slots: ['quad_isolation'],
    pattern: 'isolation',
    primary: ['quads'],
    secondary: ['abs'],
    equipment: ['mat', 'bodyweight'],
    kind: 'isolation',
    loadType: 'bodyweight',
    difficulty: 2,
    quality: 8,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 75,
    incrementKg: 0,
    setup: ['Kneel upright on a mat, knees hip width, glutes squeezed and ribs down.'],
    cues: [
      'Lean backwards from the knees, keeping a straight line from knee to shoulder.',
      'Go as far as you can control and come back up without folding at the hips.',
      'Needs nothing but floor, and it loads the quads in the stretched position better than a leg extension does.',
    ],
    mistakes: ['Breaking at the hips and sitting back onto the heels'],
    variations: ['sissy_squat', 'leg_extension'],
    imagery: {
      start: 'kneeling upright on a mat with the torso vertical and the body straight from knees to head',
      end: 'kneeling and leaned far backwards with the body in a straight line, knees bent, quads stretched',
    },
  },

  // --------------------------------------------------------- hamstrings -----
  {
    id: 'romanian_deadlift',
    name: 'Romanian deadlift',
    aka: ['RDL'],
    slots: ['ham_primary'],
    pattern: 'hinge',
    primary: ['hamstrings', 'glutes'],
    secondary: ['lower_back', 'forearms', 'traps'],
    equipment: ['barbell'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 10,
    defaultSets: 4,
    repRange: [8, 12],
    restSeconds: 150,
    incrementKg: 2.5,
    setup: [
      'Stand tall holding the bar at hip height, feet hip width, knees softly bent.',
      'Shoulders back, bar touching the thighs.',
    ],
    cues: [
      'Push your hips back towards the wall behind you — the bar slides down your legs, it does not travel forward.',
      'Stop when you feel a strong hamstring stretch, usually just below the knee. Back stays flat.',
      'Drive the hips forward to stand up. Knee angle barely changes the whole rep.',
    ],
    mistakes: ['Squatting it instead of hinging', 'Rounding the back to chase extra range'],
    variations: ['dumbbell_rdl', 'good_morning', 'cable_pull_through'],
    imagery: {
      start: 'standing tall holding a barbell at hip height against the thighs, knees slightly bent, shoulders back',
      end: 'hinged forward at the hips with the barbell just below the knees, flat back, hamstrings stretched',
    },
  },
  {
    id: 'dumbbell_rdl',
    name: 'Dumbbell Romanian deadlift',
    slots: ['ham_primary'],
    pattern: 'hinge',
    primary: ['hamstrings', 'glutes'],
    secondary: ['lower_back'],
    equipment: ['dumbbells'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 8,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 120,
    incrementKg: 2,
    setup: ['Dumbbells in front of the thighs, feet hip width.'],
    cues: ['Same hinge as the barbell version; the dumbbells let you sink a touch deeper.'],
    variations: ['romanian_deadlift', 'cable_pull_through'],
    imagery: {
      start: 'standing upright holding two dumbbells in front of the thighs, knees slightly bent',
      end: 'hinged forward with dumbbells lowered to mid shin, flat back, hips pushed back',
    },
  },
  {
    id: 'conventional_deadlift',
    name: 'Conventional deadlift',
    slots: ['ham_primary'],
    pattern: 'hinge',
    primary: ['hamstrings', 'glutes', 'lower_back'],
    secondary: ['quads', 'traps', 'lats', 'forearms'],
    equipment: ['barbell', 'plates'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 3,
    quality: 10,
    defaultSets: 3,
    repRange: [3, 6],
    restSeconds: 240,
    incrementKg: 5,
    setup: [
      'Bar over the middle of your feet, feet hip width.',
      'Hinge down and grip just outside your shins; shoulders slightly in front of the bar.',
      'Pull the slack out of the bar until you hear it click, chest up, lats tight.',
    ],
    cues: [
      'Push the floor away rather than pulling with the back.',
      'Hips and shoulders rise together. Bar stays in contact with your legs the whole way.',
      'Lock out by squeezing the glutes — do not lean back at the top.',
    ],
    mistakes: ['Hips shooting up first', 'Letting the bar drift away from the shins'],
    variations: ['romanian_deadlift', 'trap_bar_deadlift'],
    imagery: {
      start: 'set up at the bottom of a deadlift, barbell over midfoot, flat back, hips high, arms straight',
      end: 'standing fully upright at deadlift lockout holding a barbell at hip height, glutes squeezed',
    },
  },
  {
    id: 'trap_bar_deadlift',
    name: 'Trap bar deadlift',
    slots: ['ham_primary', 'quad_primary'],
    pattern: 'hinge',
    primary: ['glutes', 'quads', 'hamstrings'],
    secondary: ['traps', 'lower_back', 'forearms'],
    equipment: ['trap_bar', 'plates'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 9,
    defaultSets: 3,
    repRange: [5, 8],
    restSeconds: 210,
    incrementKg: 5,
    setup: ['Stand inside the trap bar, feet hip width, grip the neutral handles.'],
    cues: [
      'The neutral grip and centred load make this far kinder to the lower back than a straight bar.',
      'Sit down a little more than a conventional pull, then stand up tall.',
    ],
    variations: ['conventional_deadlift', 'romanian_deadlift'],
    imagery: {
      start: 'standing inside a hexagonal trap bar at the bottom position, gripping the neutral handles, flat back',
      end: 'standing upright holding a loaded trap bar at arms length by the sides, locked out',
    },
  },
  {
    id: 'good_morning',
    name: 'Good morning',
    slots: ['ham_primary'],
    pattern: 'hinge',
    primary: ['hamstrings', 'lower_back'],
    secondary: ['glutes'],
    equipment: ['barbell', 'power_rack'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 3,
    quality: 7,
    defaultSets: 3,
    repRange: [10, 12],
    restSeconds: 120,
    incrementKg: 2.5,
    setup: ['Light bar on the upper back as for a squat, feet hip width, soft knees.'],
    cues: [
      'Hinge the hips back and let the torso fold towards parallel with a flat back.',
      'Start much lighter than you think. This one punishes ego.',
    ],
    variations: ['romanian_deadlift', 'back_extension_glute'],
    imagery: {
      start: 'standing upright with a barbell across the upper back, feet hip width, ready to hinge',
      end: 'torso hinged forward towards parallel with the floor, barbell still on the upper back, flat back',
    },
  },
  {
    id: 'seated_leg_curl',
    name: 'Seated leg curl',
    slots: ['ham_isolation'],
    pattern: 'isolation',
    primary: ['hamstrings'],
    secondary: ['calves'],
    equipment: ['leg_curl_seated'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Knee pivot on the machine axis, thigh pad locked down snug.'],
    cues: [
      'Curl the heels under the seat and squeeze, then resist for three seconds on the way out.',
      'The seated version trains the hamstrings at a longer length than lying — it grows them faster.',
    ],
    variations: ['lying_leg_curl', 'nordic_curl'],
    imagery: {
      start: 'seated in a leg curl machine with legs extended forward and the pad against the back of the ankles',
      end: 'seated leg curl finished position, heels curled down and under the seat, hamstrings contracted',
    },
  },
  {
    id: 'lying_leg_curl',
    name: 'Lying leg curl',
    slots: ['ham_isolation'],
    pattern: 'isolation',
    primary: ['hamstrings'],
    secondary: ['calves'],
    equipment: ['leg_curl_lying'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Face down, knees just off the end of the pad, ankle roller on the achilles.'],
    cues: ['Curl all the way up, keep your hips pressed into the bench rather than letting them lift.'],
    variations: ['seated_leg_curl', 'nordic_curl'],
    imagery: {
      start: 'lying face down on a leg curl machine with legs straight and the roller against the achilles',
      end: 'lying face down with the heels curled up towards the glutes against the machine roller',
    },
  },
  {
    id: 'nordic_curl',
    name: 'Nordic hamstring curl',
    slots: ['ham_isolation'],
    pattern: 'isolation',
    primary: ['hamstrings'],
    secondary: ['glutes'],
    equipment: ['mat', 'bodyweight'],
    kind: 'isolation',
    loadType: 'bodyweight',
    difficulty: 3,
    quality: 8,
    defaultSets: 3,
    repRange: [4, 8],
    restSeconds: 120,
    incrementKg: 0,
    setup: ['Kneel with your ankles anchored under something solid, torso upright.'],
    cues: [
      'Lower forward as slowly as you possibly can, keeping hips extended.',
      'Catch yourself with your hands and push back up. Best hamstring injury insurance there is.',
    ],
    variations: ['seated_leg_curl', 'lying_leg_curl'],
    imagery: {
      start: 'kneeling upright on a mat with ankles anchored, torso vertical, arms crossed on the chest',
      end: 'lowered forward from kneeling with the body in a straight line close to the floor, hands ready to catch',
    },
  },

  // ------------------------------------------------------------ glutes ------
  {
    id: 'barbell_hip_thrust',
    name: 'Barbell hip thrust',
    slots: ['glutes'],
    pattern: 'hinge',
    primary: ['glutes'],
    secondary: ['hamstrings'],
    equipment: ['barbell', 'flat_bench', 'plates'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 2,
    quality: 9,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 5,
    setup: [
      'Upper back against the bench at roughly shoulder-blade height, bar over the hips with a pad.',
      'Feet flat, shins vertical at the top of the rep.',
    ],
    cues: [
      'Drive through the heels and squeeze the glutes hard at lockout.',
      'Tuck the chin and keep the ribs down — the movement is at the hips, not the lower back.',
    ],
    variations: ['machine_hip_thrust', 'cable_pull_through', 'back_extension_glute'],
    imagery: {
      start: 'seated on the floor with the upper back against a bench and a padded barbell across the hips, hips low',
      end: 'hips driven up to full lockout with the torso horizontal, barbell across the hips, shins vertical',
    },
  },
  {
    id: 'machine_hip_thrust',
    name: 'Hip thrust machine',
    slots: ['glutes'],
    pattern: 'hinge',
    primary: ['glutes'],
    secondary: ['hamstrings'],
    equipment: ['hip_thrust_machine'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Sit in, belt or pad across the hips, feet planted shoulder width.'],
    cues: ['All the benefit of a barbell thrust with none of the loading hassle. Squeeze and hold at the top.'],
    variations: ['barbell_hip_thrust', 'glute_kickback_machine'],
    imagery: {
      start: 'seated in a hip thrust machine with the hip pad in place and hips lowered',
      end: 'hips driven forward to full extension in a hip thrust machine, glutes contracted',
    },
  },
  {
    id: 'cable_kickback',
    name: 'Cable glute kickback',
    slots: ['glutes'],
    pattern: 'isolation',
    primary: ['glutes'],
    secondary: ['hamstrings'],
    equipment: ['cable_station', 'ankle_strap'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 7,
    unilateral: true,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Ankle strap on a low pulley, face the machine and hold on for balance.'],
    cues: ['Kick straight back from the hip with a soft knee; do not arch the lower back to get more range.'],
    variations: ['glute_kickback_machine', 'barbell_hip_thrust'],
    imagery: {
      start: 'standing facing a cable machine holding the frame, one ankle strapped to a low cable, leg under the body',
      end: 'one leg extended straight back behind the body against cable resistance, glute contracted',
    },
  },
  {
    id: 'glute_kickback_machine',
    name: 'Glute kickback machine',
    slots: ['glutes'],
    pattern: 'isolation',
    primary: ['glutes'],
    secondary: ['hamstrings'],
    equipment: ['glute_kickback'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 7,
    unilateral: true,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Chest on the pad, one foot on the platform.'],
    cues: ['Push the platform back and up, pause at full extension.'],
    variations: ['cable_kickback', 'machine_hip_thrust'],
    imagery: {
      start: 'leaning on the chest pad of a glute kickback machine with one foot on the platform, knee bent',
      end: 'one leg pushed back and up to full extension on a glute kickback machine',
    },
  },
  {
    id: 'hip_abduction',
    name: 'Hip abduction machine',
    slots: ['glutes'],
    pattern: 'isolation',
    primary: ['glutes'],
    secondary: [],
    equipment: ['abductor_machine'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 60,
    incrementKg: 5,
    setup: ['Sit with the pads against the outside of your knees.'],
    cues: [
      'Lean forward slightly to bias the upper glute.',
      'Push out, hold two seconds, control back in. High reps work best here.',
    ],
    variations: ['cable_kickback', 'machine_hip_thrust'],
    imagery: {
      start: 'seated at a hip abduction machine with knees together against the inner side of the pads',
      end: 'seated at a hip abduction machine with knees pushed wide apart against the pads',
    },
  },
  {
    id: 'cable_pull_through',
    name: 'Cable pull-through',
    slots: ['glutes', 'ham_primary'],
    pattern: 'hinge',
    primary: ['glutes', 'hamstrings'],
    secondary: ['lower_back'],
    equipment: ['cable_station', 'rope_attachment'],
    kind: 'compound',
    loadType: 'cable',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 75,
    incrementKg: 2.5,
    setup: ['Rope on a low pulley, face away from the machine with the rope between your legs.'],
    cues: [
      'Hinge back letting the rope pull your hips, then snap the hips forward and squeeze.',
      'Great way to learn the hinge pattern with zero spinal loading.',
    ],
    variations: ['romanian_deadlift', 'barbell_hip_thrust'],
    imagery: {
      start: 'standing facing away from a low cable, hinged forward with a rope passed between the legs',
      end: 'standing upright with hips driven forward, rope held at the hips, glutes squeezed',
    },
  },
  {
    id: 'back_extension_glute',
    name: '45° back extension',
    slots: ['glutes', 'ham_isolation'],
    pattern: 'hinge',
    primary: ['glutes', 'hamstrings'],
    secondary: ['lower_back'],
    equipment: ['back_extension'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 75,
    incrementKg: 2.5,
    setup: ['Pad just below the hip bones so your hips can bend freely.'],
    cues: [
      'Round the upper back slightly and tuck the chin to keep it on the glutes rather than the spinal erectors.',
      'Rise until your body is straight — no higher. Hug a plate to add load.',
    ],
    variations: ['cable_pull_through', 'romanian_deadlift'],
    imagery: {
      start: 'hinged down over a 45 degree back extension bench with the torso pointing towards the floor',
      end: 'raised to a straight body line on a 45 degree back extension bench, glutes squeezed',
    },
  },

  // ------------------------------------------------------------ calves ------
  {
    id: 'standing_calf_raise',
    name: 'Standing calf raise',
    slots: ['calves'],
    pattern: 'isolation',
    primary: ['calves'],
    secondary: [],
    equipment: ['calf_raise_standing'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 4,
    repRange: [10, 15],
    restSeconds: 60,
    incrementKg: 5,
    setup: ['Balls of the feet on the platform, heels hanging free, knees straight.'],
    cues: [
      'Drop the heels as far below the platform as you can and pause in the stretch for two seconds.',
      'Rise all the way onto the toes and hold. Straight knees means this hits the gastrocnemius.',
    ],
    mistakes: ['Bouncing through tiny reps with too much weight'],
    variations: ['seated_calf_raise', 'leg_press_calf_raise', 'smith_calf_raise'],
    imagery: {
      start: 'standing on a calf raise machine platform with the heels dropped low below the toes, deep stretch',
      end: 'standing on a calf raise machine risen fully onto the toes, calves contracted, knees straight',
    },
  },
  {
    id: 'seated_calf_raise',
    name: 'Seated calf raise',
    slots: ['calves'],
    pattern: 'isolation',
    primary: ['calves'],
    secondary: [],
    equipment: ['calf_raise_seated'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 45,
    incrementKg: 5,
    setup: ['Pads on the lower thighs, balls of the feet on the platform.'],
    cues: [
      'Bent knees shift the work to the soleus underneath — you need both this and the standing version.',
      'Slow and full range, two-second pause at the bottom.',
    ],
    variations: ['standing_calf_raise', 'leg_press_calf_raise'],
    imagery: {
      start: 'seated at a calf raise machine with pads on the thighs and heels dropped below the platform',
      end: 'seated calf raise with the heels pushed up high onto the toes, calves contracted',
    },
  },
  {
    id: 'leg_press_calf_raise',
    name: 'Leg press calf raise',
    slots: ['calves'],
    pattern: 'isolation',
    primary: ['calves'],
    secondary: [],
    equipment: ['leg_press'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 10,
    setup: ['Balls of the feet on the bottom edge of the leg press platform, legs nearly straight.'],
    cues: ['Keep the safeties engaged. Push the platform away with the toes only, full stretch each rep.'],
    variations: ['standing_calf_raise', 'seated_calf_raise'],
    imagery: {
      start: 'feet on the bottom edge of a leg press platform with the heels dropped and ankles flexed',
      end: 'leg press platform pushed away by pointing the toes, calves fully contracted, legs straight',
    },
  },
  {
    id: 'smith_calf_raise',
    name: 'Smith machine calf raise',
    slots: ['calves'],
    pattern: 'isolation',
    primary: ['calves'],
    secondary: [],
    equipment: ['smith_machine', 'plates'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 20],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Stand on a plate under the smith bar with the bar across your traps.'],
    cues: ['Same as the machine version — deep stretch, full squeeze, slow tempo.'],
    variations: ['standing_calf_raise', 'seated_calf_raise'],
    imagery: {
      start: 'standing on a weight plate under a smith machine bar with the heels dropped below the plate',
      end: 'risen onto the toes on a plate under a smith machine bar, calves fully contracted',
    },
  },

  // --------------------------------------------------------- adductors ------
  {
    id: 'adductor_machine_ex',
    name: 'Hip adduction machine',
    slots: ['adductors'],
    pattern: 'isolation',
    primary: ['adductors'],
    secondary: [],
    equipment: ['adductor_machine'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 45,
    incrementKg: 5,
    setup: ['Sit with the pads against the inside of your knees, legs spread.'],
    cues: [
      'Squeeze the knees together, hold, then open slowly to a stretch.',
      'Genuinely worth doing — strong adductors stabilise the knee under a heavy squat.',
    ],
    variations: ['copenhagen_plank', 'goblet_squat'],
    imagery: {
      start: 'seated at a hip adduction machine with the knees spread wide apart against the inner pads',
      end: 'seated at a hip adduction machine with the knees squeezed together against the pads',
    },
  },
  {
    id: 'copenhagen_plank',
    name: 'Copenhagen plank',
    slots: ['adductors', 'obliques'],
    pattern: 'core',
    primary: ['adductors', 'obliques'],
    secondary: ['abs'],
    equipment: ['flat_bench', 'mat'],
    kind: 'isolation',
    loadType: 'timed',
    difficulty: 3,
    quality: 7,
    unilateral: true,
    defaultSets: 3,
    repRange: [15, 30],
    restSeconds: 60,
    incrementKg: 0,
    setup: ['Side plank position with the top leg resting on a bench at knee or ankle height.'],
    cues: [
      'Lift the hips so the body is one straight line, supported by the inner thigh.',
      'Start with the knee on the bench; ankle is the hard version. Reps are seconds.',
    ],
    variations: ['adductor_machine_ex', 'side_plank'],
    imagery: {
      start: 'side plank position on the forearm with the top leg resting on a bench, hips still low',
      end: 'side plank with hips lifted into a straight line, top leg pressing down on the bench, inner thigh loaded',
    },
  },
];
